/**
 * ADR-0016 Phase 4 — Lot Projection from InventoryMovements.
 *
 * Write order: InventoryMovement → apply to lots → stockSnapshots
 *
 * Do not import stockMovementLedger (circular).
 * Do not static-import lotSystem / domain/lots (cycle: lotSystem → movements →
 * projection → lots/lotSystem → TDZ on LOT_STATUS). Lazy-require at call time.
 */

import { sameLotId } from '../../../utils/productIdUtils';
import { runWithInventoryLotProjection } from '../guards/inventoryLotMutationGuard';

// String literals — mirror STOCK_MOVEMENT_TYPES
const T = {
  PURCHASE: 'PURCHASE',
  SALE: 'SALE',
  SALE_RETURN: 'SALE_RETURN',
  TRANSFER_OUT: 'TRANSFER_OUT',
  TRANSFER_IN: 'TRANSFER_IN',
  EXPIRY: 'EXPIRY',
  ADJUSTMENT: 'ADJUSTMENT',
  CONSUMPTION: 'CONSUMPTION',
  PRODUCTION_CONSUME: 'PRODUCTION_CONSUME',
  PRODUCTION_OUTPUT: 'PRODUCTION_OUTPUT',
  VOID: 'VOID'
};

const ADD_TYPES = new Set([
  T.PURCHASE,
  T.SALE_RETURN,
  T.TRANSFER_IN,
  T.PRODUCTION_OUTPUT
]);

const SUB_TYPES = new Set([
  T.SALE,
  T.TRANSFER_OUT,
  T.EXPIRY,
  T.CONSUMPTION,
  T.PRODUCTION_CONSUME
]);

/** Signed types: quantity already encodes direction (+ receive / − reverse). */
const SIGNED_TYPES = new Set([T.ADJUSTMENT, T.VOID]);

const LOT_ACTIVE = 'active';
const LOT_SOLD_OUT = 'sold_out';
const LOT_EXPIRED = 'expired';

/** Lazy-load after cycle-breaking modules finished init. */
function lotSystemApi() {
  // eslint-disable-next-line global-require, no-restricted-imports -- call-time only, break TDZ cycle
  return require('../../../utils/lotSystem');
}

function recomputeLotMoney(lot) {
  const remaining = Number(lot.remainingQuantity) || 0;
  const buy = Number(lot.purchaseUnitPrice) || 0;
  const sell = Number(lot.sellingUnitPrice) || 0;
  lot.totalPurchaseValue = remaining * buy;
  lot.totalSellingValue = remaining * sell;
  lot.potentialProfit = lot.totalSellingValue - lot.totalPurchaseValue;
  lot.updatedAt = new Date().toISOString();
  if (remaining <= 0) {
    lot.status = LOT_SOLD_OUT;
  } else if (lot.status === LOT_SOLD_OUT || !lot.status) {
    lot.status = LOT_ACTIVE;
  }
  return lot;
}

function findLotIndex(parsed, lotId) {
  if (lotId == null || lotId === '') return -1;
  return (parsed.lots || []).findIndex((l) => sameLotId(l.id, lotId));
}

function sumProductLocationQty(parsed, productId, location) {
  const locKey = String(location || 'Principal').trim().toLowerCase();
  let sum = 0;
  for (const lot of parsed.lots || []) {
    if (!lot || String(lot.productId) !== String(productId)) continue;
    const ll = String(lot.location || 'Principal').trim().toLowerCase();
    if (ll !== locKey) continue;
    sum += Number(lot.remainingQuantity) || 0;
  }
  return sum;
}

export function applySingleMovementToLotProjection(parsed, movement) {
  if (!parsed || !movement) return { ok: false, error: 'invalid_args' };

  const { ensureLotsArray, createLot } = lotSystemApi();
  ensureLotsArray(parsed);

  const type = String(movement.movementType || '');
  const signed = Number(movement.quantity);
  if (!Number.isFinite(signed) || signed === 0) {
    return { ok: true, skipped: true };
  }

  let delta = signed;
  if (SIGNED_TYPES.has(type)) {
    delta = signed;
  } else if (ADD_TYPES.has(type) && delta < 0) {
    delta = Math.abs(delta);
  } else if (SUB_TYPES.has(type) && delta > 0) {
    delta = -Math.abs(delta);
  }

  const absQty = Math.abs(delta);
  const idx = findLotIndex(parsed, movement.lotId);

  if (idx < 0 && delta > 0) {
    const sell =
      movement.sellingUnitPrice != null
        ? Number(movement.sellingUnitPrice)
        : Number(movement.unitCost) || 0;
    const lot = createLot({
      productId: movement.productId,
      purchaseId: movement.purchaseId || movement.referenceId || null,
      purchaseUnitPrice: movement.unitCost || 0,
      sellingUnitPrice: Number.isFinite(sell) ? sell : 0,
      quantity: absQty,
      location: movement.location || 'Principal',
      batchNumber: movement.batchNumber || null,
      id: movement.lotId || null,
      purchaseDate: movement.occurredAt || movement.createdAt || new Date().toISOString(),
      supplier: movement.supplier != null ? String(movement.supplier) : '',
      expirationDate: movement.expirationDate || null
    });
    if (movement.lotId != null) lot.id = String(movement.lotId);
    if (movement.storeId != null && String(movement.storeId).trim()) {
      lot.storeId = String(movement.storeId).trim();
    }
    if (movement.isTransfer) {
      lot.isTransfer = true;
      lot.transferType = movement.transferType || 'location_transfer';
      lot.transferredFrom = movement.transferredFrom || movement.sourceLotId || null;
      lot.originalLotId = movement.originalLotId || movement.transferredFrom || movement.sourceLotId || null;
      lot.transferredAt = movement.transferredAt || new Date().toISOString();
    }
    recomputeLotMoney(lot);
    parsed.lots.push(lot);
    movement.lotId = String(lot.id);
    return { ok: true, lot, created: true };
  }

  if (idx < 0) {
    return {
      ok: false,
      error: `lot_not_found:${movement.lotId || 'null'}:${type}`
    };
  }

  const lot = { ...parsed.lots[idx] };
  const remainingBefore = Number(lot.remainingQuantity) || 0;
  const remainingAfter = remainingBefore + delta;

  if (remainingAfter < -1e-9) {
    return {
      ok: false,
      error: `insufficient_lot_qty:${lot.id}:${remainingBefore}:${delta}`
    };
  }

  lot.remainingQuantity = Math.max(0, remainingAfter);
  if (delta > 0 && (ADD_TYPES.has(type) || SIGNED_TYPES.has(type))) {
    lot.totalQuantity = (Number(lot.totalQuantity) || 0) + absQty;
  } else if (delta < 0 && SIGNED_TYPES.has(type)) {
    // Reverse intake (achat void / edit / signed VOID) reduces remaining + historical intake
    lot.totalQuantity = Math.max(0, (Number(lot.totalQuantity) || 0) - absQty);
  }
  // EXPIRY: mark dead stock on the lot projection
  if (type === T.EXPIRY && absQty > 0) {
    lot.expiredQuantity = (Number(lot.expiredQuantity) || 0) + absQty;
    lot.expiredAt = movement.occurredAt || movement.recordedAt || new Date().toISOString();
    if (movement.expiredProductName) {
      lot.expiredProductName = String(movement.expiredProductName);
    }
    if (remainingAfter <= 1e-9) {
      lot.status = LOT_EXPIRED;
    }
  }
  // Optional attribute side-updates carried on the movement (receive restock)
  if (
    Number.isFinite(Number(movement.sellingUnitPrice)) &&
    Number(movement.sellingUnitPrice) > 0
  ) {
    lot.sellingUnitPrice = Number(movement.sellingUnitPrice);
  }
  if (movement.lastPurchaseId) {
    lot.lastPurchaseId = String(movement.lastPurchaseId);
  }
  if (movement.lastReceivedAt) {
    lot.lastReceivedAt = movement.lastReceivedAt;
  }
  if (movement.storeId != null && String(movement.storeId).trim()) {
    lot.storeId = String(movement.storeId).trim();
  }
  recomputeLotMoney(lot);
  // recomputeLotMoney may force sold_out; preserve EXPIRED after full expiry
  if (type === T.EXPIRY && remainingAfter <= 1e-9) {
    lot.status = LOT_EXPIRED;
  }
  parsed.lots[idx] = lot;
  return { ok: true, lot, created: false };
}

export function applyMovementsToLotProjection(parsed, movements = [], options = {}) {
  return runWithInventoryLotProjection(() => {
    const applied = [];
    const errors = [];
    const { ensureLotsArray } = lotSystemApi();
    ensureLotsArray(parsed);

    for (const movement of movements) {
      if (!movement) continue;
      const location = movement.location || 'Principal';
      const productId = movement.productId;

      if (movement.qtyBefore == null && productId != null) {
        movement.qtyBefore = sumProductLocationQty(parsed, productId, location);
      }

      const result = applySingleMovementToLotProjection(parsed, movement);
      if (!result.ok) {
        errors.push(result.error || 'project_failed');
        if (options.strict !== false) {
          const err = new Error(result.error || 'lot_projection_failed');
          err.code = 'LOT_PROJECTION_FAILED';
          err.detail = result;
          throw err;
        }
        continue;
      }

      if (movement.qtyAfter == null && productId != null) {
        movement.qtyAfter = sumProductLocationQty(parsed, productId, location);
      }
      applied.push(movement);
    }

    return { applied, errors };
  });
}

export function movementIncreasesInventory(movement) {
  const type = String(movement?.movementType || '');
  const qty = Number(movement?.quantity) || 0;
  if (SIGNED_TYPES.has(type)) return qty > 0;
  if (ADD_TYPES.has(type)) return true;
  if (SUB_TYPES.has(type)) return false;
  return qty > 0;
}
